import React from 'react';
import {
  Alert,
  Button,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { doc, onSnapshot } from 'firebase/firestore';
import LogFormShell from '../../components/UI/LogFormShell';
import { db } from '../../services/firebase';
import {
  applyCareSourceReorganizationProposal,
  requestCareSourceReorganization,
} from './careSourceProcessingClient';
import ReorganizationProposalReview from './ReorganizationProposalReview';

const CareSourceReorganizationSheet = ({
  open,
  onClose,
  sourceNoteId,
  initialOrganizedDraft = '',
  useOriginalSource = false,
}) => {
  const [organizedDraft, setOrganizedDraft] = React.useState(initialOrganizedDraft);
  const [correction, setCorrection] = React.useState('');
  const [proposalId, setProposalId] = React.useState('');
  const [status, setStatus] = React.useState('idle');
  const [error, setError] = React.useState('');
  const [proposal, setProposal] = React.useState(null);
  const [applying, setApplying] = React.useState(false);

  React.useEffect(() => {
    setOrganizedDraft(initialOrganizedDraft);
    setCorrection('');
    setProposalId('');
    setStatus('idle');
    setError('');
    setProposal(null);
    setApplying(false);
  }, [initialOrganizedDraft, sourceNoteId]);

  React.useEffect(() => {
    if (!sourceNoteId || !proposalId) return undefined;
    return onSnapshot(
      doc(db, 'careSources', sourceNoteId, 'reorganizationProposals', proposalId),
      (snapshot) => {
        const proposal = snapshot.data() || {};
        setProposal(proposal);
        if (proposal.status) setStatus(proposal.status);
        if (proposal.error) setError(proposal.error);
      },
      () => {
        setStatus('failed');
        setError("We couldn't check this proposal's status.");
      }
    );
  }, [proposalId, sourceNoteId]);

  const handleSubmit = async () => {
    setStatus('submitting');
    setError('');
    try {
      const result = await requestCareSourceReorganization({
        sourceNoteId,
        organizedDraft,
        correction,
      });
      setProposalId(result.proposalId || '');
      setStatus(result.status || 'processing');
    } catch (requestError) {
      setStatus('failed');
      setError(requestError?.message || "We couldn't start reorganizing this capture.");
    }
  };

  const isBusy = status === 'submitting' || status === 'processing';

  const handleApply = async (selectedChanges) => {
    setApplying(true);
    setError('');
    try {
      const result = await applyCareSourceReorganizationProposal({
        sourceNoteId,
        proposalId,
        selectedChanges,
      });
      setStatus(result.status || 'applied');
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('captureez:timeline-refresh'));
      }
    } catch (applyError) {
      setError(applyError?.message || "We couldn't apply these changes.");
    } finally {
      setApplying(false);
    }
  };

  return (
    <LogFormShell
      open={open}
      onClose={onClose}
      title="Reorganize with Lifelog"
      subtitle={useOriginalSource
        ? 'Preview a fresh organization of this email. Your original email will not change.'
        : 'Correct what Lifelog understood. Your original capture will not change.'}
      forceDrawer
      compactTitle
      bodySx={{ px: { xs: 1.5, sm: 3 }, pt: 1.5, pb: 3 }}
    >
      <Stack spacing={2}>
        {status === 'review_ready' ? <Alert severity="success">Your proposed changes are ready to review.</Alert> : null}
        {status === 'applied' ? <Alert severity="success">Your selected changes were applied to the Timeline.</Alert> : null}
        {error ? <Alert severity="error">{error}</Alert> : null}
        {isBusy ? (
          <Alert icon={<CircularProgress size={18} />} severity="info">
            Lifelog is preparing proposed changes. You can close this sheet; your Timeline will stay as it is.
          </Alert>
        ) : null}

        {!useOriginalSource && status !== 'review_ready' && status !== 'applied' ? <TextField
          label="Organized capture"
          value={organizedDraft}
          onChange={(event) => setOrganizedDraft(event.target.value)}
          multiline
          minRows={7}
          disabled={isBusy || status === 'review_ready'}
          helperText="Edit the details, wording, dates, or moments that Lifelog should organize."
          fullWidth
        /> : null}
        {!useOriginalSource && status !== 'review_ready' && status !== 'applied' ? <TextField
          label="Tell Lifelog what should change (optional)"
          value={correction}
          onChange={(event) => setCorrection(event.target.value)}
          multiline
          minRows={3}
          disabled={isBusy || status === 'review_ready'}
          inputProps={{ maxLength: 4000 }}
          fullWidth
        /> : null}

        {status !== 'review_ready' && status !== 'applied' ? <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>
          {useOriginalSource
            ? 'Lifelog will review each message in the saved email separately. Nothing changes until you approve the preview.'
            : 'This creates a proposal only. Nothing will be added, edited, or removed from the Timeline until you review and approve it.'}
        </Typography> : null}
        {status !== 'review_ready' && status !== 'applied' ? (
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={isBusy || (!useOriginalSource && !organizedDraft.trim())}
            sx={{ alignSelf: 'flex-start' }}
          >
            {status === 'failed' ? 'Try again' : useOriginalSource ? 'Preview organized results' : 'Prepare proposed changes'}
          </Button>
        ) : null}
        {status === 'review_ready' ? (
          <ReorganizationProposalReview
            changes={proposal?.changes || []}
            needsDateItems={proposal?.needsDateItems || []}
            replaceAll={proposal?.replacementMode === 'source_graph'}
            applying={applying}
            onApply={handleApply}
          />
        ) : null}
      </Stack>
    </LogFormShell>
  );
};

export default CareSourceReorganizationSheet;
